import {Button, FormControl} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {RefObject} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {GetSalesPlatformsApiRes, useGenerateDescriptionMutation} from '@dms/api/sales';
import {useGetDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {TITLE_EXTENSION_MAX_LENGTH} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DescriptionGeneratorInputs} from '../types/DescriptionGeneratorInputs';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';
import {buildGenerateBody} from '../utils/buildGenerateBody';
import {isSupportedLanguage, toLanguage} from '../utils/toLanguage';
import {DescriptionFormField} from './DescriptionFormField';
import {SubtitleFormField} from './SubtitleFormField';
import {TitleFormField} from './TitleFormField';

interface LanguageTabContentProps extends TestIdProps {
  control: FormControl<GeneralSettingsFormValues>;
  languageCode: string;
  vehicleId: string;
  isDisabled: boolean;
  platforms: GetSalesPlatformsApiRes | undefined;
  formApi: UseFormReturn<GeneralSettingsFormValues>;
  descriptionGeneratorInputsRef: RefObject<DescriptionGeneratorInputs | null>;
}

export function LanguageTabContent(props: LanguageTabContentProps) {
  const [generateDescription, {isLoading: isGenerating}] = useGenerateDescriptionMutation();
  const {data: descriptionGeneratorConfig} = useGetDescriptionGeneratorConfigQuery({});

  const isGeneratorEnabled = descriptionGeneratorConfig?.enabled === true;

  const handleGenerate = () => {
    if (
      isNil(descriptionGeneratorConfig) ||
      !isGeneratorEnabled ||
      !isSupportedLanguage(props.languageCode)
    ) {
      return;
    }

    const liveInputs = props.descriptionGeneratorInputsRef.current ?? undefined;
    const body = buildGenerateBody(descriptionGeneratorConfig, liveInputs);

    generateDescription({
      vehicleId: props.vehicleId,
      body: {...body, language: toLanguage(props.languageCode)},
    })
      .unwrap()
      .then((response) => {
        props.formApi.setValue(
          `localizedContent.${props.languageCode}.description`,
          response.content,
          {shouldDirty: true}
        );
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <TitleFormField
        control={props.control}
        name={`localizedContent.${props.languageCode}.titleExtension`}
        maxLength={TITLE_EXTENSION_MAX_LENGTH}
        isDisabled={props.isDisabled}
        platforms={props.platforms}
        vehicleId={props.vehicleId}
        language={isSupportedLanguage(props.languageCode) ? props.languageCode : undefined}
        isGenerateDisabled={!isSupportedLanguage(props.languageCode)}
        getCurrentValue={() =>
          props.formApi.getValues(`localizedContent.${props.languageCode}.titleExtension`) ?? ''
        }
        onGenerated={(text) => {
          props.formApi.setValue(`localizedContent.${props.languageCode}.titleExtension`, text, {
            shouldDirty: true,
          });
        }}
      />
      <SubtitleFormField
        control={props.control}
        name={`localizedContent.${props.languageCode}.subtitle`}
        isDisabled={props.isDisabled}
        platforms={props.platforms}
        data-testid={suffixTestId(`subtitle-${props.languageCode}`, props)}
      />
      <DescriptionFormField
        control={props.control}
        nameOverride={`localizedContent.${props.languageCode}.description`}
        descriptionLabel={i18n.t('page.advertisement.labels.localizedDescription')}
        vehicleId={props.vehicleId}
        isDisabled={props.isDisabled}
      />
      <HStack>
        <Button
          variant="link"
          leftIcon={isGenerating ? 'action/ai_robot' : 'action/ai'}
          isDisabled={
            props.isDisabled ||
            isGenerating ||
            !isGeneratorEnabled ||
            !isSupportedLanguage(props.languageCode)
          }
          isLoading={isGenerating}
          title={
            isGenerating
              ? i18n.t('entity.advertisingPlatforms.labels.generating')
              : i18n.t('entity.advertisingPlatforms.labels.generate')
          }
          onClick={handleGenerate}
          data-testid={suffixTestId(`generate-${props.languageCode}`, props)}
        />
      </HStack>
    </VStack>
  );
}
